import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { SectionHeader } from "@/components/careers/SectionHeader";
import { ROLE_GUIDES } from "@/lib/careers/roleGuides";
import { jobsBoardUrl } from "@/lib/careers/links";

const Column = ({ label, children }) => <div><p className="font-mono-label text-[10px] text-[#71747B] mb-2">{label}</p>{children}</div>;

const GuideBody = ({ guide }) => (
  <div data-testid={`guide-body-${guide.slug}`} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 px-6 md:px-8 pb-8 text-sm text-[#3D4049] leading-relaxed border-t hairline pt-6">
    <Column label="What you'll do"><ul className="space-y-1.5">{guide.whatYouDo.map((item) => <li key={item}>· {item}</li>)}</ul></Column>
    <Column label="Typical working conditions"><p>{guide.conditions}</p><p className="font-mono-label text-[10px] text-[#71747B] mt-5 mb-2">Possible next roles</p><p>{guide.nextRoles.join(" → ")}</p></Column>
    <Column label="Required skills"><ul className="flex flex-wrap gap-1.5">{guide.skills.map((skill) => <li key={skill} className="chrome-tile px-2 py-1 font-mono-label text-[10px] text-[#0F1117]">{skill}</li>)}</ul></Column>
    <Column label="Jargon, translated"><dl className="space-y-2">{guide.jargon.map((item) => <div key={item.term}><dt className="font-semibold text-[#0F1117] inline">{item.term}: </dt><dd className="inline">{item.meaning}</dd></div>)}</dl></Column>
    <div className="md:col-span-2 flex flex-wrap gap-3 pt-2">
      <Link to={jobsBoardUrl({ category: guide.family, early: guide.entryFriendly || undefined })} data-testid={`guide-jobs-link-${guide.slug}`} className="chrome-btn-dark inline-flex items-center gap-2 px-4 py-2.5 font-mono-label text-[10px] !tracking-normal">See {guide.family} openings<ArrowRight size={13} aria-hidden="true" /></Link>
      <Link to={`/careers?family=${encodeURIComponent(guide.family)}#prep`} data-testid={`guide-prep-link-${guide.slug}`} className="chrome-btn inline-flex items-center gap-2 px-4 py-2.5 font-mono-label text-[10px] !tracking-normal">Prepare an application</Link>
    </div>
  </div>
);

export const RoleGuides = () => {
  const location = useLocation();
  const [openSlug, setOpenSlug] = useState(null);
  useEffect(() => {
    const slug = new URLSearchParams(location.search).get("guide");
    if (slug && ROLE_GUIDES.some((guide) => guide.slug === slug)) {
      setOpenSlug(slug);
      requestAnimationFrame(() => document.getElementById(`guide-${slug}`)?.scrollIntoView({ block: "center", behavior: "smooth" }));
    }
  }, [location.search]);

  return (
    <section id="role-guides" data-testid="careers-guides-section" aria-labelledby="careers-guides-title" className="border-b hairline bg-[#F4F5F7] scroll-mt-[124px]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-28">
        <SectionHeader id="careers-guides" label="Role guides" headline="Roles in the industry," accent="in plain English." intro="What you'll actually do, the conditions you'll work in, the skills that matter and where each role leads, without the jargon. Open any guide, then jump to the live openings for that specialism." />
        <div className="border-t hairline bg-white">
          {ROLE_GUIDES.map((guide) => {
            const open = openSlug === guide.slug;
            return (
              <article key={guide.slug} id={`guide-${guide.slug}`} data-testid={`guide-${guide.slug}`} className="border-b hairline scroll-mt-24">
                <button type="button" data-testid={`guide-toggle-${guide.slug}`} aria-expanded={open} aria-controls={`guide-panel-${guide.slug}`} onClick={() => setOpenSlug(open ? null : guide.slug)} className="w-full flex items-center justify-between gap-4 px-6 md:px-8 py-5 text-left hover:bg-[#F7F8F9] transition-colors">
                  <div className="min-w-0">
                    <p className="font-mono-label text-[10px] text-[#71747B] mb-1">{guide.family}</p>
                    <h3 className="font-display text-lg md:text-xl font-bold text-[#0F1117] tracking-tight leading-tight">{guide.title}</h3>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {guide.entryFriendly && <span data-testid={`guide-entry-${guide.slug}`} className="hidden sm:inline-flex items-center gap-1.5 chrome-tile px-2.5 py-1 font-mono-label text-[10px] text-[#0F1117]"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#0FBF77]" aria-hidden />Entry-friendly</span>}
                    <ChevronDown size={18} aria-hidden="true" className={`text-[#5F6168] transition-transform ${open ? "rotate-180" : ""}`} />
                  </div>
                </button>
                <div id={`guide-panel-${guide.slug}`} hidden={!open}>{open && <GuideBody guide={guide} />}</div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
